import { Fragment, useMemo, useState } from 'react';
import { PinIcon } from './icons';
import { getViewMeta } from './ViewRegistry';
import type { DragState, DropTarget, LeafNode } from './types';

interface Props {
  leaf: LeafNode;
  onClose: (leafId: string, tabId: string) => void;
  onPin: (leafId: string, tabId: string) => void;
  onActivate: (leafId: string, tabId: string) => void;
  onDragEnd: (sourceLeafId: string, tabId: string, target: DropTarget) => void;
  dragState: DragState | null;
  setDragState: (s: DragState | null) => void;
}

export function TabStrip({ leaf, onClose, onPin, onActivate, onDragEnd, dragState, setDragState }: Props) {
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  // Pinned tabs go to the front, preserving relative order.
  const orderedTabs = useMemo(() => {
    const pinned = leaf.tabs.filter((t) => t.pinned);
    const rest = leaf.tabs.filter((t) => !t.pinned);
    return [...pinned, ...rest];
  }, [leaf.tabs]);

  const onSlotDragOver = (idx: number) => (e: React.DragEvent) => {
    if (!dragState) return;
    e.preventDefault();
    e.stopPropagation();
    setDropIndex(idx);
  };

  const onSlotDrop = (idx: number) => (e: React.DragEvent) => {
    if (!dragState) return;
    e.preventDefault();
    e.stopPropagation();
    setDropIndex(null);
    onDragEnd(dragState.sourceLeafId, dragState.tabId, { type: 'leaf-tabs', leafId: leaf.id, index: idx });
    setDragState(null);
  };

  const onStripDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDropIndex(null);
  };

  const Indicator = () => (
    <div style={{ width: 2, alignSelf: 'stretch', background: 'var(--accent)', borderRadius: 1, margin: '0 -1px' }} />
  );

  return (
    <div
      onDragLeave={onStripDragLeave}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        background: 'var(--bg-alt2)',
        borderBottom: '1px solid var(--border)',
        minHeight: 34,
        maxHeight: 34,
        overflowX: 'auto',
        flexShrink: 0,
      }}
    >
      <div onDragOver={onSlotDragOver(0)} onDrop={onSlotDrop(0)} style={{ width: 6, display: 'flex', alignItems: 'center' }}>
        {dropIndex === 0 && <Indicator />}
      </div>
      {orderedTabs.map((tab, i) => {
        const isActive = tab.id === leaf.activeId;
        const meta = getViewMeta(tab.viewId);
        return (
          <Fragment key={tab.id}>
            <div
              draggable
              onDragStart={(e) => {
                setDragState({ sourceLeafId: leaf.id, tabId: tab.id });
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', tab.id);
              }}
              onDragEnd={() => {
                setDragState(null);
                setDropIndex(null);
              }}
              onClick={() => onActivate(leaf.id, tab.id)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                onPin(leaf.id, tab.id);
              }}
              onAuxClick={(e) => {
                if (e.button === 1) {
                  e.preventDefault();
                  onClose(leaf.id, tab.id);
                }
              }}
              title={`${tab.title}${tab.pinned ? ' · pinned' : ''}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: tab.pinned ? '0 6px 0 10px' : '0 6px 0 12px',
                borderRight: '1px solid var(--border-soft)',
                background: isActive ? 'var(--bg)' : 'transparent',
                borderTop: isActive ? '2px solid var(--accent)' : '2px solid transparent',
                color: 'var(--text)',
                fontSize: 12,
                cursor: 'grab',
                minWidth: 0,
                maxWidth: tab.pinned ? 140 : 220,
                position: 'relative',
                opacity: dragState?.tabId === tab.id ? 0.4 : 1,
              }}
            >
              <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>{meta?.icon ?? '·'}</span>
              <span
                style={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontStyle: tab.ephemeral ? 'italic' : 'normal',
                  fontWeight: isActive ? 500 : 400,
                  color: isActive ? 'var(--text)' : 'var(--text-soft)',
                }}
              >
                {tab.title}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPin(leaf.id, tab.id);
                }}
                title={tab.pinned ? 'Unpin tab' : 'Pin tab'}
                aria-label={tab.pinned ? 'Unpin tab' : 'Pin tab'}
                style={{
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 0,
                  background: 'transparent',
                  color: tab.pinned ? 'var(--accent)' : 'var(--text-mute)',
                  cursor: 'pointer',
                  borderRadius: 3,
                  padding: 0,
                  opacity: tab.pinned || isActive ? 1 : 0.55,
                }}
              >
                <PinIcon filled={tab.pinned} />
              </button>
              {!tab.pinned && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(leaf.id, tab.id);
                  }}
                  title="Close tab"
                  aria-label="Close tab"
                  style={{
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 0,
                    background: 'transparent',
                    color: 'var(--text-mute)',
                    cursor: 'pointer',
                    borderRadius: 3,
                    fontSize: 13,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              )}
            </div>
            <div onDragOver={onSlotDragOver(i + 1)} onDrop={onSlotDrop(i + 1)} style={{ width: 6, display: 'flex', alignItems: 'center' }}>
              {dropIndex === i + 1 && <Indicator />}
            </div>
          </Fragment>
        );
      })}
      <div
        onDragOver={onSlotDragOver(orderedTabs.length)}
        onDrop={onSlotDrop(orderedTabs.length)}
        style={{ flex: 1, display: 'flex', alignItems: 'center' }}
      >
        {dropIndex === orderedTabs.length && orderedTabs.length > 0 && <Indicator />}
      </div>
    </div>
  );
}
